import { useEffect, useRef } from 'react';

export interface ToastData {
  message: string;
  kind: 'success' | 'error';
}

interface ToastProps extends ToastData {
  onClose: () => void;
}

/**
 * Minimal fixed bottom-center toast: auto-dismisses after 4s, the ✕ closes it
 * immediately. Held via plain useState in whichever component shows it — no
 * provider/context machinery.
 */
export default function Toast({ message, kind, onClose }: ToastProps) {
  // Keep the latest onClose in a ref so parents can pass inline closures
  // without resetting the 4s timer on every re-render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => onCloseRef.current(), 4000);
    return () => clearTimeout(timer);
  }, [message, kind]);

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        maxWidth: 'min(560px, calc(100vw - 32px))',
        padding: '10px 14px',
        borderRadius: 10,
        background: kind === 'success' ? '#1d7a46' : '#b3261e',
        color: '#fff',
        fontSize: 14,
        fontWeight: 500,
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
      }}
    >
      <span>{message}</span>
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
          fontSize: 14,
          padding: 2,
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
}
